import { useEffect } from "react";
import { BrowserRouter as Router, Route, Routes } from "react-router-dom";
import { AuthProvider } from "@/providers/auth-provider";
import { LanguageProvider } from "@/providers/language-provider";
import { ThemeProvider } from "@/providers/theme-provider";
import { Toaster } from "@/components/ui/toaster";
import { PrivateRoute } from "@/features/private-route";
import { MessageBoxComponent } from "@/components/blocks/message-box";
import { WindowFocusMonitor } from "@/features/window-focus-monitor";
import { PWAController } from "@/features/pwa-controller";
import { PWASubscription } from "@/features/pwa-subscription";
import { ServiceWorkerMessageHandler } from "@/features/service-worker-message";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { ApolloProvider } from "@apollo/client";
import { client as graphQLClient } from "@/lib/client-graphql";

import HomePage from "@/pages/home-page";
import AuthPage from "@/pages/auth-page";
import MainPage from "@/pages/main-page";
import NotFound from "@/pages/not-found";
import SharePage from "@/pages/share-page";

import "./App.css";
import "./animate.css";

// Create a react-query client (TanStack Query)
const queryClient = new QueryClient();

function App() {
  useEffect(() => {
    const setVh = () => {
      document.documentElement.style.setProperty(
        "--vh",
        `${window.innerHeight}px`,
      );
    };

    setVh();
    window.addEventListener("resize", setVh);

    return () => window.removeEventListener("resize", setVh);
  }, []);

  return (
    <>
      {/* 1. 전역 모니터링 컴포넌트들 */}
      <WindowFocusMonitor />
      <PWAController intervalMS={30 * 60 * 1000} useCustomUI={true} />
      {/* 2. 기본 Provider들 */}
      <Router>
        <ApolloProvider client={graphQLClient}>
          <QueryClientProvider client={queryClient}>
            <AuthProvider>
              <ThemeProvider defaultTheme="dark" storageKey="ui-theme">
                <LanguageProvider storageKey="ui-language">
                  {/* 3. 구독/알림/토스트 컴포넌트 */}
                  <Toaster />
                  <MessageBoxComponent />
                  <PWASubscription />
                  <ServiceWorkerMessageHandler />
                  {/* 4. 라우팅 및 메인 UI */}
                  <Routes>
                    <Route path="/" element={<HomePage />} />
                    <Route path="/auth" element={<AuthPage />} />
                    <Route
                      path="/main"
                      element={
                        <PrivateRoute>
                          <MainPage />
                        </PrivateRoute>
                      }
                    />
                    <Route path="/share" element={<SharePage />} />
                    <Route path="*" element={<NotFound />} />
                  </Routes>
                </LanguageProvider>
              </ThemeProvider>
            </AuthProvider>
          </QueryClientProvider>
        </ApolloProvider>
      </Router>
    </>
  );
}

export default App;
